import type { BadgeTone } from './ui';

/**
 * Rotulos e tons dos badges de status.
 *
 * Moram fora de ui.tsx de proposito: aquele arquivo so exporta componentes, e
 * misturar funcao pura com componente quebra o fast refresh do Vite — o modulo
 * inteiro deixa de ser elegivel e a tela recarrega do zero a cada edicao.
 */

/**
 * Rótulo PT-BR de cada `subscriptionStatus` da API (o enum `SubscriptionStatus` do Tenancy, que
 * a API manda pelo nome). É o único mapa desses rótulos: o selo, a coluna, o seletor de loja e os
 * filtros leem daqui, e a tela nunca mostra o valor cru ("Trial", "PastDue"). "Em teste grátis" é
 * o termo do livro de marca para o estado em teste (change vapt-menu-brand-book, A12).
 */
export const SUBSCRIPTION_STATUS_LABELS = {
  Trial: 'Em teste grátis',
  Active: 'Ativo',
  PastDue: 'Em atraso',
  Cancelled: 'Cancelado',
  Expired: 'Expirado',
} as const;

export type SubscriptionStatus = keyof typeof SUBSCRIPTION_STATUS_LABELS;

/**
 * O rótulo do status para a tela. Um valor que a API passe a mandar e este mapa ainda não
 * conheça aparece como veio, em vez de sumir; sem status, um travessão.
 */
export function subscriptionStatusLabel(status: string | null | undefined): string {
  if (!status) return '—';
  return Object.hasOwn(SUBSCRIPTION_STATUS_LABELS, status)
    ? SUBSCRIPTION_STATUS_LABELS[status as SubscriptionStatus]
    : status;
}

/** As opções de um filtro de status, na ordem pedida, com o rótulo do mapa. */
export function subscriptionStatusOptions(
  statuses: readonly SubscriptionStatus[],
): Array<{ value: SubscriptionStatus; label: string }> {
  return statuses.map((value) => ({ value, label: SUBSCRIPTION_STATUS_LABELS[value] }));
}

const SUBSCRIPTION_TONES: Record<SubscriptionStatus, BadgeTone> = {
  Trial: 'warning',
  Active: 'success',
  PastDue: 'error',
  Cancelled: 'neutral',
  Expired: 'error',
};

/** Mapeia o subscriptionStatus da API para rótulo PT-BR + tom visual. */
export function subscriptionBadge(status: string): { label: string; tone: BadgeTone } {
  const tone = Object.hasOwn(SUBSCRIPTION_TONES, status)
    ? SUBSCRIPTION_TONES[status as SubscriptionStatus]
    : 'neutral';
  return { label: subscriptionStatusLabel(status), tone };
}

/**
 * Mapeia o status de pedido para rótulo PT-BR + tom visual.
 *
 * Os casos são exatamente os valores de `OrderStatus` serializados pela API
 * platform (nomes do enum). Rótulos para estados que o enum não tem — "Aceito",
 * "Entregue", "Recusado" — só sugeririam um fluxo que não existe.
 */
export function orderBadge(status: string): { label: string; tone: BadgeTone } {
  switch (status) {
    case 'Pending': return { label: 'Pendente', tone: 'warning' };
    case 'Preparing': return { label: 'Em preparo', tone: 'info' };
    case 'ReadyForPickup': return { label: 'Pronto', tone: 'primary' };
    case 'OutForDelivery': return { label: 'Em entrega', tone: 'primary' };
    case 'Completed': return { label: 'Concluído', tone: 'success' };
    case 'Cancelled': return { label: 'Cancelado', tone: 'error' };
    default: return { label: status, tone: 'neutral' };
  }
}

